import {
  ANT,
  ARIOWrite,
  ArNSBuyAntState,
  FundFrom,
  MessageResult,
  PrimaryName,
  SetPrimaryNameProgressEvents,
} from '@ar.io/sdk/web';
import { TurboArNSClient } from '@src/services/turbo/TurboArNSClient';
import { TransactionAction } from '@src/state/reducers/TransactionReducer';
import {
  ARNS_INTERACTION_TYPES,
  AoAddress,
  ArNSWalletConnector,
  ContractInteraction,
} from '@src/types';
import { lowerCaseDomain, sleep } from '@src/utils';
import { APP_NAME, WRITE_OPTIONS } from '@src/utils/constants';
import eventEmitter from '@src/utils/events';
import { queryClient } from '@src/utils/network';
import { arioContractCacheKey } from '@src/utils/sdk-init';
import {
  getActiveSolanaConfig,
  getSolanaRpc,
  getSolanaRpcSubscriptions,
} from '@src/utils/solana';
import { createAntStateForOwner } from '@src/utils/transactionUtils/transactionUtils';
import { Dispatch } from 'react';

export default async function dispatchArIOInteraction({
  payload,
  workflowName,
  owner,
  arioContract,
  processId,
  dispatch,
  wallet,
  fundFrom,
  paidBy,
  // `promoCode` was used by the legacy Stripe-funded ArNS purchase
  // path (`TurboArNSClient.executeArNSIntent`), which is currently
  // disabled — accept and ignore so call sites don't have to change.
  promoCode: _promoCode,
}: {
  payload: Record<string, any>;
  workflowName: ARNS_INTERACTION_TYPES;
  owner: AoAddress;
  arioContract?: ARIOWrite;
  processId: string;
  dispatch: Dispatch<TransactionAction>;
  /**
   * Connected Solana wallet — provides the kit signer used for ANT spawning
   * and on-chain ArNS purchase instructions.
   */
  wallet?: ArNSWalletConnector;
  fundFrom?: FundFrom | 'fiat';
  paidBy?: string[];
  promoCode?: string;
  // Legacy AO/Turbo args — accepted but ignored to soften the cross-phase
  // landing. `signer` (AO ContractSigner), `ao` (AoClient), `scheduler`,
  // `hyperbeamUrl`, `turboArNSClient` were used by the AO-spawn + Stripe-ArNS
  // paths that are gone after de-AO.
  signer?: unknown;
  ao?: unknown;
  scheduler?: string;
  hyperbeamUrl?: string;
  turboArNSClient?: TurboArNSClient;
}): Promise<ContractInteraction> {
  let result: MessageResult<MessageResult | unknown> | undefined = undefined;
  try {
    if (!arioContract) throw new Error('ArIO provider is not defined');
    if (wallet?.tokenType !== 'solana' || !wallet.solanaSigner) {
      throw new Error(
        'A connected Solana wallet with a signer is required for ArIO interactions.',
      );
    }
    // The Stripe-funded ArNS purchase flow (`executeArNSIntent`) is currently
    // AO-coupled and disabled on Solana — see `TurboArNSClient.ts`. Surface a
    // clear error if anyone reaches the `'fiat'` branch despite the UI gate.
    if (fundFrom === 'fiat') {
      throw new Error(
        'Credit-card payments for ArNS purchases are temporarily unavailable on Solana. The Turbo payment service needs Solana support before this flow can be re-enabled. Top up Turbo credits with SOL/ARIO and use `fundFrom: "turbo"` instead.',
      );
    }
    // Pass the user's chosen funding mode through unchanged. The SDK's
    // `'stakes'`/`'withdrawal'` branches route through the funding planner
    // (constrained to the chosen mode) when no explicit `gatewayAddress` /
    // `withdrawalId` is supplied, so a bare "Staked Balances" selection draws
    // only from delegations/vaults — never silently from liquid balance.
    const originalFundFrom = fundFrom as FundFrom;
    dispatch({ type: 'setSigning', payload: true });
    switch (workflowName) {
      case ARNS_INTERACTION_TYPES.BUY_RECORD: {
        const { name, type, years } = payload;
        const lowered = lowerCaseDomain(name);

        // Returned-name auction names use a different on-chain instruction
        // (`buy_returned_name`, which splits the proceeds between the original
        // initiator and the protocol). Check the registry before the ANT spawn
        // — `getArNSReturnedName` throws / returns nullish when the name isn't
        // in the auction, in which case we fall through to the regular
        // `buyRecord` path. Don't let a transient RPC failure wedge the buy:
        // if the lookup errors, log and assume "not returned" rather than
        // blocking the purchase.
        let isReturnedName = false;
        try {
          const returned = await arioContract.getArNSReturnedName({
            name: lowered,
          });
          isReturnedName = !!returned;
        } catch (error) {
          console.debug(
            `[dispatchArIOInteraction] getArNSReturnedName lookup failed for '${lowered}' — proceeding with buyRecord path`,
            error,
          );
        }

        // The user's existing ANT, when they chose to reuse one on the
        // registration form. Undefined means "mint a fresh ANT for this name".
        const existingAntProcessId: string | undefined = payload.processId;

        if (isReturnedName) {
          // `buyReturnedName` does NOT support atomic ANT spawning (unlike
          // `buyRecord`); it requires a real `processId`. So for returned names
          // we still spawn the ANT first.
          let antProcessId: string;
          if (existingAntProcessId) {
            antProcessId = existingAntProcessId;
          } else {
            dispatch({
              type: 'setSigningMessage',
              payload: `Spawning new ANT for returned name '${name}'`,
            });
            const { programIds } = getActiveSolanaConfig();
            const spawnResult = await ANT.spawn({
              rpc: getSolanaRpc(),
              rpcSubscriptions: getSolanaRpcSubscriptions(),
              signer: wallet.solanaSigner,
              antProgramId: programIds.antProgramId,
              state: {
                ...createAntStateForOwner(owner.toString()),
                name,
              },
            });
            antProcessId = spawnResult.processId;
          }

          dispatch({
            type: 'setSigningMessage',
            payload: `Purchasing returned name '${name}' from auction`,
          });
          // `buyReturnedName` is Solana-only — same pattern as the
          // `releaseName`/`reassignName` casts in `dispatchANTInteraction`.
          // Phase 7 should hoist it onto the shared `ARIOWrite` interface.
          try {
            result = await (arioContract as any).buyReturnedName({
              name: lowered,
              type,
              years,
              processId: antProcessId,
              fundFrom: originalFundFrom,
              referrer: APP_NAME,
              paidBy,
            });
          } catch (buyError: any) {
            // If we spawned a fresh ANT but the purchase failed, tell the
            // user about the orphaned ANT so they can reuse it on retry.
            if (!existingAntProcessId) {
              const msg = buyError?.message ?? String(buyError);
              throw new Error(
                `Failed to purchase returned name '${name}': ${msg}. ` +
                  `A new ANT was already created (${antProcessId}). ` +
                  `You can reuse it by selecting it under Advanced Options when retrying.`,
              );
            }
            throw buyError;
          }
          payload.processId = antProcessId;
        } else {
          // Atomic purchase (ar.io SDK >= 4.1.0-alpha.5): when no `processId` is
          // supplied, `buyRecord` mints a fresh ANT and assigns the name to it
          // in the SAME transaction, returning the new ANT id as
          // `result.result.processId`. The SDK routes balance/credit-funded
          // buys inline (one signature) and only falls back to a multi-tx
          // Address Lookup Table for large stake-funded plans that exceed the
          // 1232-byte tx limit. Only pass `processId` when reusing an existing
          // ANT; otherwise omit it and let the SDK spawn.
          dispatch({
            type: 'setSigningMessage',
            payload: existingAntProcessId
              ? `Purchasing '${name}'`
              : `Purchasing '${name}' and spawning its ANT`,
          });
          result = await arioContract.buyRecord({
            name: lowered,
            type,
            years,
            ...(existingAntProcessId
              ? { processId: existingAntProcessId }
              : {
                  antState: createAntStateForOwner(
                    owner.toString(),
                  ) as ArNSBuyAntState,
                }),
            fundFrom: originalFundFrom,
            referrer: APP_NAME,
            paidBy,
          });
          // Surface the ANT (reused, or freshly spawned and reported by the
          // SDK) so the Checkout success screen and transaction history can
          // link to it.
          payload.processId =
            existingAntProcessId ??
            (result as any)?.result?.processId ??
            undefined;

          if (!payload.processId) {
            throw new Error(
              `Name '${name}' was purchased (tx: ${result?.id}) but the SDK did not return the ANT process ID. Check the transaction on-chain or contact support.`,
            );
          }
        }

        dispatch({
          type: 'setSigningMessage',
          payload: `Successfully purchased '${name}'`,
        });
        break;
      }
      case ARNS_INTERACTION_TYPES.EXTEND_LEASE:
        result = await arioContract.extendLease(
          {
            name: lowerCaseDomain(payload.name),
            years: payload.years,
            fundFrom: originalFundFrom,
            referrer: APP_NAME,
            paidBy,
          },
          WRITE_OPTIONS,
        );
        break;
      case ARNS_INTERACTION_TYPES.INCREASE_UNDERNAMES:
        result = await arioContract.increaseUndernameLimit(
          {
            name: lowerCaseDomain(payload.name),
            increaseCount: payload.qty,
            fundFrom: originalFundFrom,
            referrer: APP_NAME,
            paidBy,
          },
          WRITE_OPTIONS,
        );
        break;
      case ARNS_INTERACTION_TYPES.PRIMARY_NAME_REQUEST: {
        result = await arioContract.setPrimaryName(
          {
            name: payload.name,
            fundFrom: originalFundFrom,
            referrer: APP_NAME,
            paidBy,
          },
          {
            ...WRITE_OPTIONS,
            onSigningProgress: (
              step: keyof SetPrimaryNameProgressEvents,
              progressPayload: SetPrimaryNameProgressEvents[keyof SetPrimaryNameProgressEvents],
            ) => {
              if (step === 'requesting-primary-name') {
                dispatch({
                  type: 'setSigningMessage',
                  payload: `Requesting primary name '${(progressPayload as any)?.name ?? payload.name}'`,
                });
              } else if (step === 'request-already-exists') {
                dispatch({
                  type: 'setSigningMessage',
                  payload: `Primary name request for '${payload.name}' already exists!`,
                });
              } else if (step === 'approving-request') {
                dispatch({
                  type: 'setSigningMessage',
                  payload: `Approving primary name request for '${payload.name}'`,
                });
              }
            },
          },
        );

        dispatch({
          type: 'setSigningMessage',
          payload: `Confirming primary name has been updated`,
        });

        // wait 3 seconds and check if the primary name is set
        await sleep(3000);
        await queryClient.refetchQueries({
          predicate: ({ queryKey }) =>
            queryKey.includes('primary-name') &&
            queryKey[1] === owner.toString(),
        });
        const updatedPrimaryName = queryClient.getQueryData<PrimaryName>([
          'primary-name',
          owner.toString(),
          arioContractCacheKey(arioContract),
        ]);
        if (!updatedPrimaryName || updatedPrimaryName.name !== payload.name) {
          dispatch({
            type: 'setSigningMessage',
            payload: `Primary name updated. It may take a few minutes to reflect due to network delays. Please check back in a few minutes.`,
          });
          await sleep(3000);
        } else {
          dispatch({
            type: 'setSigningMessage',
            payload: `Successfully set primary name '${payload.name}'!`,
          });
        }
        break;
      }
      case ARNS_INTERACTION_TYPES.UPGRADE_NAME: {
        dispatch({
          type: 'setSigningMessage',
          payload: 'Upgrading Name to Permabuy',
        });
        result = await arioContract.upgradeRecord(
          {
            name: payload.name,
            fundFrom: originalFundFrom,
            referrer: APP_NAME,
            paidBy,
          },
          WRITE_OPTIONS,
        );
        break;
      }
      default:
        throw new Error(`Unsupported workflow name: ${workflowName}`);
    }
  } catch (error) {
    eventEmitter.emit('error', error);
  } finally {
    dispatch({ type: 'setSigning', payload: false });
    // Invalidate balances + every cache that backs the affected name.
    // `useDomainInfo` internally `fetchQuery`s both `arns-record` and
    // `['ant', processId, …]` with long staleTimes, so just busting the
    // outer `domainInfo` key isn't enough — the nested `fetchQuery`s
    // would just return the stale cached values (e.g. an out-of-date
    // `endTimestamp` after EXTEND_LEASE, or stale undername counts after
    // INCREASE_UNDERNAMES). Bust the inner caches too.
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        queryKey.includes('io-balance') ||
        queryKey.includes('ario-liquid-balance') ||
        queryKey.includes('ario-delegated-stake') ||
        queryKey.includes('turbo-credit-balance') ||
        queryKey.includes('arns-records') ||
        queryKey[0] === 'arns-record' ||
        queryKey.includes('domainInfo') ||
        queryKey[0] === 'ant' ||
        queryKey[0] === 'ant-info' ||
        queryKey.includes(lowerCaseDomain(payload.name)),
      refetchType: 'active',
    });
  }
  if (!result) {
    throw new Error('Failed to dispatch ArIO interaction');
  }

  const interaction: ContractInteraction = {
    deployer: owner.toString(),
    processId: processId.toString(),
    id: result.id,
    type: 'interaction',
    payload,
  };

  dispatch({ type: 'setWorkflowName', payload: workflowName });
  dispatch({ type: 'setInteractionResult', payload: interaction });
  return interaction;
}
